import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';
import { STORAGE_KEYS, MESSAGES, ROUTES } from '../constants/Constants';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Initialize auth state from localStorage
    useEffect(() => {
        const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (error) {
                console.error('Error parsing stored user:', error);
                localStorage.removeItem(STORAGE_KEYS.USER);
            }
        }
        setLoading(false);
    }, []);

    const login = async (email, password) => {
        try {
            setLoading(true);
            setError(null);
            const response = await authApi.login({ email, password });
            
            if (response.success && response.user) {
                localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.user));
                localStorage.setItem(STORAGE_KEYS.TOKEN, response.token);
                setUser(response.user);
                navigate(ROUTES.DASHBOARD);
                return { success: true };
            } else {
                throw new Error(response.message || MESSAGES.ERROR.LOGIN_FAILED);
            }
        } catch (error) {
            setError(error.message || MESSAGES.ERROR.GENERAL);
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    };

    const register = async (userData) => {
        try {
            setLoading(true);
            setError(null);
            const response = await authApi.register(userData);
            
            if (response.success) {
                // Automatically log in after successful registration
                return await login(userData.email, userData.password);
            } else {
                throw new Error(response.message || 'Registration failed');
            }
        } catch (error) {
            setError(error.message || MESSAGES.ERROR.GENERAL);
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            setLoading(true);
            await authApi.logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem(STORAGE_KEYS.USER);
            localStorage.removeItem(STORAGE_KEYS.TOKEN);
            setUser(null);
            setLoading(false);
            navigate(ROUTES.LOGIN);
        }
    };

    const updateUser = (userData) => {
        const updatedUser = { ...user, ...userData };
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
        setUser(updatedUser);
    };

    const value = {
        user,
        loading,
        error,
        login,
        logout,
        register,
        updateUser,
        isAuthenticated: !!user
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
